import { useCallback, useMemo, useRef, useState } from 'react';
import type { DateRange } from 'react-day-picker';
import type { GridApi, ModelUpdatedEvent, RowDoubleClickedEvent } from '@ag-grid-community/core';
import { endOfDay, format } from 'date-fns';
import { CalendarDays, FileSpreadsheet, Filter, Loader2, RotateCcw } from 'lucide-react';
import { toast } from 'sonner';
import { utils, writeFile } from 'xlsx';
import AGDataGrid from '@/components/ag-data-grid';
import CalendarRangePicker from '@/components/calendar-range-picker';
import type { ChipOption } from '@/components/chip-group';
import { ChipGroup, FacetLabel } from '@/components/chip-group';
import Container from '@/components/container';
import { CopyCountButton } from '@/components/copy-count-button';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { MultiCombobox } from '@/components/ui/multi-combobox';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { useGrid } from '@/contexts/grid-context';
import { useRawTransactions } from '@/hooks/use-raw-transactions';
import type { RawTransaction } from '@/hooks/use-raw-transactions';
import { autoFitColumns } from '@/lib/sheetjs-patches';
import { transactionColumnDefs } from './components/transaction-grid-columns';
import TransactionSheet from './components/transaction-sheet';

const FACETS = ['operation', 'category', 'tld', 'period', 'registrar'] as const;
type Facet = typeof FACETS[number];

function emptyFacetFilters(): Record<Facet, Set<string>> {
  return {
    operation: new Set(),
    category: new Set(),
    tld: new Set(),
    period: new Set(),
    registrar: new Set(),
  };
}

const TLD_OPTIONS: ChipOption[] = [
  { value: 'bank', label: '.Bank' },
  { value: 'insurance', label: '.Insurance' },
];

const ROW_SELECTION = { mode: 'singleRow' } as const;

function getRowId(params: { data: RawTransaction }) {
  return params.data.id;
}

function facetKeys({ domain, data }: RawTransaction): Record<Facet, string> {
  return {
    operation: data.operation ?? '',
    // Registry data mixes casing, e.g. "STANDARD" alongside "premium"
    category: (data.category ?? '').toUpperCase(),
    tld: domain.split('.').pop()?.toLowerCase() ?? '',
    period: data.period === undefined ? '' : String(data.period),
    registrar: data.registrar ?? '',
  };
}

function toTitleCaseOptions(values: string[]): ChipOption[] {
  return values.map((value) => ({
    value,
    label: value ? value.toLowerCase().replaceAll(/\b\w/g, (l) => l.toUpperCase()) : '(Blank)',
  }));
}

function parseAmount(value: string): number | undefined {
  const amount = Number.parseFloat(value);
  return Number.isFinite(amount) ? amount : undefined;
}

function formatTimestamp(value: number | undefined, pattern: string): string {
  return value ? format(new Date(value), pattern) : '';
}

function RawTransactionsPage() {
  const [searchQuery, setSearchQuery] = useState('');
  const [dateRange, setDateRange] = useState<DateRange>();
  const [costMin, setCostMin] = useState('');
  const [costMax, setCostMax] = useState('');
  const [facetFilters, setFacetFilters] = useState(emptyFacetFilters);
  const [selectedTransaction, setSelectedTransaction] = useState<RawTransaction | null>(null);
  const [isSheetOpen, setIsSheetOpen] = useState(false);
  const [displayedRows, setDisplayedRows] = useState<RawTransaction[]>([]);
  const [hasColumnFilter, setHasColumnFilter] = useState(false);
  const gridApiRef = useRef<GridApi<RawTransaction> | null>(null);
  const { registerGridApi } = useGrid();
  const { data: transactions, isLoading } = useRawTransactions();
  const allTransactions = useMemo(() => transactions ?? [], [transactions]);

  const matchesDateAndCost = useMemo(() => {
    const start = dateRange?.from?.getTime();
    const end = dateRange?.from ? endOfDay(dateRange.to ?? dateRange.from).getTime() : undefined;
    const min = parseAmount(costMin);
    const max = parseAmount(costMax);
    const hasCostFilter = min !== undefined || max !== undefined;
    if (start === undefined && !hasCostFilter)
      return;
    return ({ data }: RawTransaction) => {
      if (start !== undefined && end !== undefined && (data.transactionDate < start || data.transactionDate > end))
        return false;
      if (!hasCostFilter)
        return true;
      // DELETE transactions carry no cost, so any cost bound excludes them
      if (data.cost === undefined)
        return false;
      return (min === undefined || data.cost >= min) && (max === undefined || data.cost <= max);
    };
  }, [dateRange, costMin, costMax]);

  const keyedRows = useMemo(
    () => allTransactions.map((row) => ({ row, keys: facetKeys(row) })),
    [allTransactions],
  );

  // Options come from every row so a selected value never drops out of view.
  const facetOptions = useMemo(() => {
    const result = {} as Record<Facet, string[]>;
    for (const f of FACETS) {
      const counts = new Map<string, number>();
      for (const { keys } of keyedRows)
        counts.set(keys[f], (counts.get(keys[f]) ?? 0) + 1);
      result[f] = [...counts.entries()].sort((a, b) => b[1] - a[1]).map(([value]) => value);
    }
    return result;
  }, [keyedRows]);

  const scopedRows = useMemo(
    () => (matchesDateAndCost ? keyedRows.filter(({ row }) => matchesDateAndCost(row)) : keyedRows),
    [keyedRows, matchesDateAndCost],
  );

  const matchesFacets = useCallback(
    (keys: Record<Facet, string>, skip?: Facet) =>
      FACETS.every((f) => f === skip || facetFilters[f].size === 0 || facetFilters[f].has(keys[f])),
    [facetFilters],
  );

  const filteredRows = useMemo(
    () => scopedRows.filter(({ keys }) => matchesFacets(keys)).map(({ row }) => row),
    [scopedRows, matchesFacets],
  );

  // Each facet's counts apply the other facets' selections but not its own.
  const facetCounts = useMemo(() => {
    const result = {} as Record<Facet, Record<string, number>>;
    for (const f of FACETS) {
      const counts: Record<string, number> = {};
      for (const { keys } of scopedRows) {
        if (matchesFacets(keys, f))
          counts[keys[f]] = (counts[keys[f]] ?? 0) + 1;
      }
      result[f] = counts;
    }
    return result;
  }, [scopedRows, matchesFacets]);

  const toggleFacet = useCallback((key: Facet, value: string) => {
    setFacetFilters((prev) => {
      const next = new Set(prev[key]);
      if (next.has(value))
        next.delete(value);
      else next.add(value);
      return { ...prev, [key]: next };
    });
  }, []);

  const earliestDate = useMemo(() => {
    let earliest = Number.POSITIVE_INFINITY;
    for (const { data } of allTransactions)
      earliest = Math.min(earliest, data.transactionDate);
    return Number.isFinite(earliest) ? new Date(earliest) : undefined;
  }, [allTransactions]);

  const operationOptions = useMemo(() => toTitleCaseOptions(facetOptions.operation), [facetOptions]);
  const categoryOptions = useMemo(() => toTitleCaseOptions(facetOptions.category), [facetOptions]);
  const periodOptions = useMemo<ChipOption[]>(
    () => facetOptions.period
      .toSorted((a, b) => (a === '' ? 1 : (b === '' ? -1 : Number(a) - Number(b))))
      .map((value) => ({ value, label: value || '(Blank)' })),
    [facetOptions],
  );
  const registrarOptions = useMemo<ChipOption[]>(
    () => facetOptions.registrar.map((value) => ({
      value,
      label: `${value || '(Blank)'} (${(facetCounts.registrar[value] ?? 0).toLocaleString()})`,
    })),
    [facetOptions, facetCounts],
  );

  const handleRowDoubleClick = (event: RowDoubleClickedEvent<RawTransaction>) => {
    if (event.data) {
      setSelectedTransaction(event.data);
      setIsSheetOpen(true);
    }
  };

  const handleSheetOpenChange = (open: boolean) => {
    setIsSheetOpen(open);
    if (!open) {
      setSelectedTransaction(null);
    }
  };

  const handleGridReady = (params: { api: GridApi<RawTransaction> }) => {
    gridApiRef.current = params.api;
    registerGridApi(params.api);
  };

  // Mirror what the grid shows (every filter in the card plus search and column
  // filters, in sort order) so the count and export always match the visible rows.
  const handleModelUpdated = useCallback((event: ModelUpdatedEvent<RawTransaction>) => {
    const rows: RawTransaction[] = [];
    event.api.forEachNodeAfterFilterAndSort((node) => {
      if (node.data)
        rows.push(node.data);
    });
    setDisplayedRows(rows);
    setHasColumnFilter(event.api.isColumnFilterPresent());
  }, []);

  const hasActiveFilters = FACETS.some((f) => facetFilters[f].size > 0)
    || dateRange !== undefined
    || costMin !== ''
    || costMax !== ''
    || searchQuery.trim() !== ''
    || hasColumnFilter;

  const clearFilters = () => {
    setFacetFilters(emptyFacetFilters());
    setDateRange(undefined);
    setCostMin('');
    setCostMax('');
    setSearchQuery('');
    gridApiRef.current?.setFilterModel(null);
  };

  const handleExport = useCallback(() => {
    if (displayedRows.length === 0) {
      toast.error('No transactions to export');
      return;
    }

    try {
      const exportRows = displayedRows.map(({ domain, data }) => ({
        'Transaction Date': formatTimestamp(data.transactionDate, 'MM/dd/yyyy h:mm a'),
        'Domain': domain,
        'Operation': data.operation ?? '',
        'Category': data.category ?? '',
        'Registrar': data.registrar ?? '',
        'IANA ID': data.ianaId ?? '',
        'Cost (USD)': data.cost ?? '',
        'Period (Years)': data.period ?? '',
        'Start Date': formatTimestamp(data.startDate, 'MM/dd/yyyy'),
        'End Date': formatTimestamp(data.endDate, 'MM/dd/yyyy'),
        'Transaction ID': data.transactionId ?? '',
        'Server Transaction ID': data.serverTransactionId ?? '',
        'Domain ROID': data.domainRoid ?? '',
      }));

      const wb = utils.book_new();
      const ws = utils.json_to_sheet(exportRows);
      ws['!cols'] = autoFitColumns(exportRows);
      utils.book_append_sheet(wb, ws, 'Transactions');

      const rangeLabel = dateRange?.from
        ? `${format(dateRange.from, 'yyyyMMdd')}_${format(dateRange.to ?? dateRange.from, 'yyyyMMdd')}`
        : 'all_time';
      const filename = `raw_transactions_${rangeLabel}_${format(new Date(), 'yyyyMMdd_HHmm')}.xlsx`;
      writeFile(wb, filename);
      toast.success(`Exported ${exportRows.length.toLocaleString()} transactions to ${filename}`);
    } catch {
      toast.error('Failed to export transactions');
    }
  }, [displayedRows, dateRange]);

  return (
    <Container maxWidth={false} className="pb-12">
      <div className="mb-6 flex items-start justify-between gap-4">
        <div>
          <h1 className="mt-2 text-xl font-bold">Raw Registry Transactions</h1>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={handleExport}
          disabled={isLoading || displayedRows.length === 0}
          className="gap-2"
        >
          <FileSpreadsheet className="size-4" />
          Export
          {' '}
          {displayedRows.length.toLocaleString()}
          {' '}
          transactions
        </Button>
      </div>

      {isLoading
        ? (
            <Card>
              <CardContent className="flex items-center justify-center py-12">
                <div className="flex items-center gap-3 text-muted-foreground">
                  <Loader2 className="size-5 animate-spin" />
                  <span>Loading transactions...</span>
                </div>
              </CardContent>
            </Card>
          )
        : (
            <>
              <Card className="mb-4 border-dashed">
                <CardContent className="py-4">
                  <div className="flex flex-col gap-4">
                    <div className="flex flex-wrap gap-x-8 gap-y-4">
                      <ChipGroup
                        label="Operation"
                        options={operationOptions}
                        selected={facetFilters.operation}
                        onToggle={(v) => toggleFacet('operation', v)}
                        counts={facetCounts.operation}
                      />
                      <ChipGroup
                        label="Category"
                        options={categoryOptions}
                        selected={facetFilters.category}
                        onToggle={(v) => toggleFacet('category', v)}
                        counts={facetCounts.category}
                      />
                      <ChipGroup
                        label="TLD"
                        options={TLD_OPTIONS}
                        selected={facetFilters.tld}
                        onToggle={(v) => toggleFacet('tld', v)}
                        counts={facetCounts.tld}
                      />
                      <ChipGroup
                        label="Period (Years)"
                        options={periodOptions}
                        selected={facetFilters.period}
                        onToggle={(v) => toggleFacet('period', v)}
                        counts={facetCounts.period}
                      />
                    </div>

                    <div className="flex flex-wrap gap-x-8 gap-y-4 border-t pt-3">
                      <div className="flex flex-col gap-1.5">
                        <FacetLabel>Registrar</FacetLabel>
                        <div className="w-72">
                          <MultiCombobox
                            options={registrarOptions}
                            selected={[...facetFilters.registrar]}
                            onChange={(values) => setFacetFilters((prev) => ({ ...prev, registrar: new Set(values) }))}
                            placeholder="Select registrars..."
                            emptyText="No registrars found."
                          />
                        </div>
                      </div>
                      <div className="flex flex-col gap-1.5">
                        <FacetLabel>Cost (USD)</FacetLabel>
                        <div className="flex items-center gap-1.5">
                          <Input
                            type="number"
                            inputMode="decimal"
                            min={0}
                            placeholder="Min"
                            value={costMin}
                            onChange={(e) => setCostMin(e.target.value)}
                            className="h-8 w-24 text-xs"
                          />
                          <span className="text-xs text-muted-foreground">–</span>
                          <Input
                            type="number"
                            inputMode="decimal"
                            min={0}
                            placeholder="Max"
                            value={costMax}
                            onChange={(e) => setCostMax(e.target.value)}
                            className="h-8 w-24 text-xs"
                          />
                        </div>
                      </div>
                      <div className="flex flex-col gap-1.5">
                        <FacetLabel>Date Range</FacetLabel>
                        <Popover>
                          <PopoverTrigger asChild>
                            <Button variant="outline" size="sm" className="h-8 gap-2 text-xs font-normal">
                              <CalendarDays className="size-3.5" />
                              {dateRange?.from
                                ? `${format(dateRange.from, 'MMM d, yyyy')} – ${format(dateRange.to ?? dateRange.from, 'MMM d, yyyy')}`
                                : 'All dates'}
                            </Button>
                          </PopoverTrigger>
                          <PopoverContent className="w-auto p-0" align="start">
                            <CalendarRangePicker
                              value={dateRange ?? { from: undefined }}
                              onValueChange={(range) => setDateRange(range?.from ? range : undefined)}
                              fromDate={earliestDate}
                              captionLayout="dropdown"
                              className="border-0"
                            />
                            <div className="border-t p-2">
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => setDateRange(undefined)}
                                disabled={!dateRange}
                                className="w-full text-xs"
                              >
                                Clear dates
                              </Button>
                            </div>
                          </PopoverContent>
                        </Popover>
                      </div>
                    </div>

                    <div className="flex items-center justify-between gap-3 border-t pt-3">
                      <div className="flex items-center gap-2">
                        {hasActiveFilters
                          ? (
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={clearFilters}
                                className="gap-1.5 text-xs text-muted-foreground"
                              >
                                <RotateCcw className="size-3" />
                                Clear filters
                              </Button>
                            )
                          : (
                              <span className="text-xs text-muted-foreground">
                                Use the filters above, or search by domain or registrar
                              </span>
                            )}
                      </div>

                      <div className="flex items-center gap-3">
                        <CopyCountButton count={displayedRows.length} label="transactions" />

                        <div className="relative w-56">
                          <Input
                            type="search"
                            placeholder="Search domains or registrars..."
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            className="h-8 pl-8 text-xs"
                          />
                          <Filter className="absolute left-2.5 top-1/2 size-3.5 -translate-y-1/2 text-muted-foreground" />
                        </div>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <AGDataGrid
                columnDefs={transactionColumnDefs}
                rowData={filteredRows}
                quickFilterText={searchQuery}
                getRowId={getRowId}
                rowSelection={ROW_SELECTION}
                onRowDoubleClicked={handleRowDoubleClick}
                onGridReady={handleGridReady}
                onModelUpdated={handleModelUpdated}
                overlayNoRowsTemplate='<span class="ag-overlay-no-rows-center">No transactions match the selected filters</span>'
              />
            </>
          )}

      <TransactionSheet
        open={isSheetOpen}
        onOpenChange={handleSheetOpenChange}
        transaction={selectedTransaction || undefined}
      />
    </Container>
  );
}

export default RawTransactionsPage;
